/**
 * Funciones de autenticacion para el frontend.
 * Access token in memory (Zustand), refresh token in httpOnly cookie.
 */
import { api, clearAuth, saveAccessToken } from '../services/api';
import { useAuthStore } from '../stores/authStore';

// Tipos
export interface LoginCredentials {
  email: string;
  password: string;
  env?: string;  // Ambiente de BD (multi-env mode)
}

export interface EnvironmentInfo {
  key: string;
  label: string;
  color: string;
  is_primary: boolean;
}

export interface EnvironmentsResponse {
  environments: EnvironmentInfo[];
  multi_env: boolean;
}

export interface AccessTokenResponse {
  access_token: string;
  token_type: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  nombre: string;
  apellido?: string;
  tipo: 'INTERNO' | 'CLIENTE';
  avatar_url?: string;
  cliente_id?: string;
  roles: string[];
}

export interface AuthResult {
  success: boolean;
  error?: string;
  user?: CurrentUser;
}

/**
 * Obtener ambientes disponibles (endpoint público).
 */
export async function fetchEnvironments(): Promise<EnvironmentsResponse> {
  try {
    return await api.get<EnvironmentsResponse>('/auth/environments');
  } catch {
    return { environments: [], multi_env: false };
  }
}

/**
 * Realizar login con email y password.
 * Backend sets refresh token as httpOnly cookie; access token comes in JSON.
 */
export async function login(
  credentials: LoginCredentials,
  envInfo?: EnvironmentInfo,
): Promise<AuthResult> {
  try {
    const tokenData = await api.post<AccessTokenResponse>('/auth/login', credentials, false);

    // Guardar access token en memoria (Zustand)
    saveAccessToken(tokenData.access_token);

    // Obtener datos del usuario
    const user = await api.get<CurrentUser>('/auth/me');

    // Actualizar store con user + access token
    const authStore = useAuthStore.getState();
    authStore.login(user, tokenData.access_token);

    // Si multi-env, guardar ambiente activo en store
    if (envInfo) {
      authStore.setActiveEnv(envInfo);
    }

    return { success: true, user };
  } catch (error: unknown) {
    clearAuth();
    const message = error instanceof Error ? error.message : 'Error de autenticacion';
    return { success: false, error: message };
  }
}

/**
 * Cerrar sesion.
 * Backend reads refresh token from httpOnly cookie and revokes it.
 */
export async function logout(): Promise<void> {
  try {
    // Revocar refresh token (cookie se envia automaticamente)
    await api.post('/auth/logout', undefined, false);
  } catch (error) {
    console.warn('Server-side logout failed:', error);
  }

  // Limpiar estado
  clearAuth();
}

/**
 * Obtener datos del usuario actual.
 */
export async function getCurrentUser(): Promise<CurrentUser | null> {
  try {
    const user = await api.get<CurrentUser>('/auth/me');

    // Actualizar store
    useAuthStore.getState().setUser(user);

    return user;
  } catch {
    return null;
  }
}

/**
 * Iniciar flujo OAuth con Google.
 * Redirige al usuario al endpoint del backend que a su vez redirige a Google.
 */
export function startGoogleOAuth(): void {
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
  window.location.href = `${apiBase}/auth/google`;
}

/**
 * Iniciar flujo OAuth con Microsoft.
 * Redirige al usuario al endpoint del backend que a su vez redirige a Microsoft.
 */
export function startMicrosoftOAuth(): void {
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
  window.location.href = `${apiBase}/auth/microsoft`;
}

/**
 * Completar login OAuth procesando los tokens recibidos del callback.
 */
export async function completeOAuthLogin(accessToken: string): Promise<AuthResult> {
  saveAccessToken(accessToken);

  try {
    const user = await api.get<CurrentUser>('/auth/me');

    const authStore = useAuthStore.getState();
    authStore.login(user, accessToken);

    return { success: true, user };
  } catch (error: unknown) {
    clearAuth();
    const message = error instanceof Error ? error.message : 'Error obteniendo datos del usuario';
    return { success: false, error: message };
  }
}

/**
 * Verificar si el usuario esta autenticado y cargar datos.
 */
export async function checkAuth(): Promise<boolean> {
  const authStore = useAuthStore.getState();
  authStore.setLoading(true);

  try {
    const user = await getCurrentUser();

    if (user) {
      return true;
    }

    // No autenticado, limpiar estado
    clearAuth();
    return false;
  } catch {
    clearAuth();
    return false;
  } finally {
    authStore.setLoading(false);
  }
}
